// Copyright (c) Docugami, Inc. All rights reserved.
import { useEffect, useState } from 'react';
import { PrimaryButton, DefaultButton, TextField, Modal, IIconProps, IconButton, IButtonStyles, Toggle } from "@fluentui/react";
import { greyText, homeGray, primaryTheme } from '../presentation/AppStyles';
import { dgFontFamily, userFontFamily } from 'components/common/Fonts';
import styled from 'styled-components';


const StyledTextField = styled(TextField)`
    .ms-TextField-fieldGroup:after {
        border-color: ${homeGray};
    }
`
const ModalTitle = styled.div`
    font-family: ${dgFontFamily};
    font-weight: 600;
    font-size: 18px;
    padding-left: 10px;
`

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    flex: '1 1 auto';
    align-items: center;
`

const ModalContent = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const ModalBody = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0px 12px;
`

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    padding: 0px 12px 12px 12px;
`

const Spacer = styled.div`
    flex-grow: 1;
`

const HelpText = styled.span`
    font-family: ${dgFontFamily};
    font-size: 12px;
    font-weight: 500;
`

const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
      color: primaryTheme.palette.neutralPrimary,
      marginLeft: 'auto',
      marginRight: '2px',
      alignItems: 'center',
    },
    rootHovered: {
      color: primaryTheme.palette.neutralDark,
    },
  };

const cancelIcon: IIconProps = { iconName: 'Cancel' };

export interface TextEntryDialogProps {
    hidden: boolean;
    originalText: string;
    title: string;
    description?: string;
    multiline?: boolean;
    rows?: number;
    width?: string | number | undefined;
    placeholderText: string;
    confirmButtonText: string;
    dismissButtonText: string;
    revertButtonText?: string;
    toggle?: TextEntryDialogToggleProps;
    dismissedCallback: () => void;
    confirmedCallback?: (value: string) => void;
    revertCallback?: () => void;
    confirmedButtonDisabled?: (value: string) => boolean;
    actionInProgress?: boolean;
}

export interface TextEntryDialogToggleProps {
    onText: string;
    offText: string;
    label?: string;
    checked: boolean;
    confirmedCallback: (value: string, toggle: boolean) => void;
    confirmedButtonDisabled?: (value: string, toggle: boolean) => boolean;
}

export const TextEntryDialog = (props: TextEntryDialogProps): JSX.Element => {
    const [text, setText] = useState('');
    const [toggle, setToggle] = useState(false);

    useEffect(() => {
        if (!props.hidden) {
            setText(props.originalText);
            if (props.toggle) {
                setToggle(props.toggle.checked);
            }
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.hidden])

    function onKeyPress(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onCommit();
        }
    }

    function onCommit() {
        if (!!props.toggle) {
            props.toggle.confirmedCallback(text, toggle);
        } else if (!!props.confirmedCallback) {
            props.confirmedCallback(text);
        }
    }

    function isConfirmButtonDisabled(): boolean {
        if (props.actionInProgress) {
            return true;
        }

        if (!!props.toggle?.confirmedButtonDisabled) {
            return props.toggle.confirmedButtonDisabled(text, toggle);
        }

        if (!!props.confirmedButtonDisabled) {
            return props.confirmedButtonDisabled(text);
        }

        return false;
    }

    function dialog(): JSX.Element {
        if (props.hidden) {
            return <div />
        }

        return <Modal
            isOpen={true}
            onDismiss={() => props.dismissedCallback()}
            isBlocking={true}
            styles={{
                root: {
                    overflow: 'hidden',
                },
                main: {
                    overflow: 'hidden',
                    borderRadius: '5px',
                    background: primaryTheme.palette.neutralLighterAlt,
                    padding: '0px 0px 0px 0px',
                    minHeight: '100px',
                },
                scrollableContent: {
                    overflow: 'hidden',
                    maxHeight: '75vh',
                    maxWidth: '50vw',
                    minWidth: props.width ?? '400px',
                    height: '100%'
                }
            }}>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>{props.title}</ModalTitle>
                    <IconButton
                        styles={iconButtonStyles}
                        iconProps={cancelIcon}
                        ariaLabel="Close popup modal"
                        onClick={() => props.dismissedCallback()}/>
                </ModalHeader>
                <ModalBody>
                    {props.description && <HelpText>{props.description}</HelpText>}
                    {props.toggle && <Toggle
                                key="Toggle"
                                checked={toggle}
                                label={props.toggle.label ?? ''}
                                onText={props.toggle.onText}
                                offText={props.toggle.offText}
                                onChange={(ev, value) => setToggle(!!value)} />}
                    <StyledTextField
                        disabled={props.actionInProgress}
                        value={text as string}
                        placeholder={props.placeholderText}
                        onChange={(e, t) => setText(t as string)}
                        onKeyPress={onKeyPress}
                        multiline={props.multiline}
                        rows={props.rows}
                        styles={{
                            fieldGroup: {
                                borderColor: greyText,
                                fontFamily: userFontFamily,
                            }
                        }}
                    />
                </ModalBody>
                <ModalFooter>
                    {props.revertButtonText && props.revertCallback && <DefaultButton
                        onClick={() => props.revertCallback?.()}
                        text={props.revertButtonText}
                        disabled={props.actionInProgress}
                        styles={{
                            root: {
                                color: greyText,
                                border: 'none'
                            }
                        }} />}
                    <Spacer />
                    <DefaultButton
                        onClick={props.dismissedCallback}
                        text={props.dismissButtonText}
                        disabled={props.actionInProgress}
                        styles={{
                            root: {
                                color: greyText,
                                border: 'none'
                            }
                        }} />
                    <PrimaryButton
                        onClick={onCommit}
                        text={props.confirmButtonText}
                        disabled={isConfirmButtonDisabled()} />
                </ModalFooter>
            </ModalContent>
        </Modal>
    }

    return dialog()
}
